import * as React from 'react'
import { Plus } from 'lucide-react'

export interface QuoteBuilderLineItemsCardProps {
  sectionId: string
  /** The primary (non-transportation) line items table. */
  itemsTable: React.ReactNode
  transportationSection: React.ReactNode
  /** Muted hint for the transportation sub-header (mileage source / rate). */
  transportationHint: string
  /** Keeps the section mounted whenever a mileage charge already exists or the org includes one by default. */
  transportationActive: boolean
  addonsTable: React.ReactNode
  addonCount: number
  discountControl: React.ReactNode
  taxControl: React.ReactNode
  tipControl: React.ReactNode
  disabled?: boolean
}

function SubSectionHeader({ label, hint }: { label: string; hint?: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="text-sm font-semibold">{label}</h3>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function DashedAddButton({
  label,
  disabled,
  onClick,
}: {
  label: string
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-xl border border-dashed px-4 py-3 text-left text-sm text-muted-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
    >
      <Plus className="size-4 shrink-0" aria-hidden="true" />
      {label}
    </button>
  )
}

/**
 * Line Items card: the committed items table, then transportation and optional
 * add-ons as clearly labelled sub-sections, then the document-wide
 * discount / sales tax / enforced tip controls.
 */
export function QuoteBuilderLineItemsCard({
  sectionId,
  itemsTable,
  transportationSection,
  transportationHint,
  transportationActive,
  addonsTable,
  addonCount,
  discountControl,
  taxControl,
  tipControl,
  disabled = false,
}: QuoteBuilderLineItemsCardProps) {
  const [transportationRevealed, setTransportationRevealed] = React.useState(false)
  const [addonsRevealed, setAddonsRevealed] = React.useState(false)
  const showTransportation = transportationRevealed || transportationActive
  const showAddons = addonsRevealed || addonCount > 0

  return (
    <section
      id={sectionId}
      aria-labelledby={`${sectionId}-heading`}
      tabIndex={-1}
      className="scroll-mt-24 space-y-5 rounded-2xl border bg-card p-5 focus:outline-none"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={`${sectionId}-heading`} className="text-base font-semibold">Line Items</h2>
        <p className="text-xs text-muted-foreground">Drag to reorder · per-item tax and unit</p>
      </div>

      {itemsTable}

      <div className="space-y-3 border-t pt-5">
        <SubSectionHeader label="Transportation" hint={transportationHint} />
        {showTransportation ? (
          transportationSection
        ) : (
          <DashedAddButton
            label="Add transportation"
            disabled={disabled}
            onClick={() => setTransportationRevealed(true)}
          />
        )}
      </div>

      <div className="space-y-3 border-t pt-5">
        <SubSectionHeader label="Optional Add-ons" hint="Client can opt in at checkout" />
        {showAddons ? (
          <div className="space-y-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 p-4">
            {addonsTable}
            <p className="text-xs text-muted-foreground">
              Not included in the total until the client accepts.
            </p>
          </div>
        ) : (
          <DashedAddButton
            label="Add an optional add-on"
            disabled={disabled}
            onClick={() => setAddonsRevealed(true)}
          />
        )}
      </div>

      <div className="grid min-w-0 gap-4 border-t pt-5 sm:grid-cols-3 [&>*]:min-w-0">
        {discountControl}
        {taxControl}
        {tipControl}
      </div>
    </section>
  )
}
